"use client";

import type { AssetFilter } from "@saas/shared/history/types";
import { PICKER_ASSET_FILTERS } from "@saas/shared/history/types";
import { Spinner } from "@shared/components/Spinner";
import { Button } from "@ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import { cn } from "@ui/lib";
import { CheckIcon, ImageOffIcon, Loader2Icon, UploadIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { AssetToolbar } from "./AssetToolbar";
import { groupAssetsByDate, useAssetLibrary } from "./useAssetLibrary";
import { useUploadAsset } from "./useUploadAsset";

interface SelectAssetDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** How many more images the caller can still take */
	remainingSlots: number;
	/** Reference insertion can reuse these images without taking a new slot. */
	existingUrls?: string[];
	/** Called with the picked image URLs when the user confirms */
	onConfirm: (urls: string[]) => void;
}

/** Drops and pastes can carry text or other files — only images go to My Assets */
function imageFiles(list: FileList | null | undefined): File[] {
	return Array.from(list ?? []).filter((file) =>
		file.type.startsWith("image/"),
	);
}

export function SelectAssetDialog({
	open,
	onOpenChange,
	remainingSlots,
	existingUrls = [],
	onConfirm,
}: SelectAssetDialogProps) {
	const t = useTranslations("assets.picker");
	const tAssets = useTranslations("assets");
	const [tab, setTab] = useState<AssetFilter>("all");
	const [selected, setSelected] = useState<string[]>([]);
	// Expired R2 URLs would otherwise render as blank tiles you can still click
	const [brokenUrls, setBrokenUrls] = useState<string[]>([]);
	const [isDragOver, setIsDragOver] = useState(false);
	const loadMoreRef = useRef<HTMLDivElement | null>(null);
	const newImageCount = (urls: string[]) =>
		urls.filter((url) => !existingUrls.includes(url)).length;

	// Uploading from inside the picker should behave like picking: the new image
	// lands in My Assets and is staged right away.
	const upload = useUploadAsset({
		onUploaded: (urls) =>
			setSelected((previous) => [
				...previous,
				...urls.slice(
					0,
					Math.max(remainingSlots - newImageCount(previous), 0),
				),
			]),
	});

	const {
		items,
		isLoading,
		error,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	} = useAssetLibrary({ filter: tab, enabled: open });

	// Reopening should never carry over the previous selection
	useEffect(() => {
		if (!open) {
			setSelected([]);
			setTab("all");
			setIsDragOver(false);
		}
	}, [open]);

	// "Drop or paste anywhere": listen on the window while open, so a drop on
	// the overlay uploads too instead of the browser opening the image
	useEffect(() => {
		if (!open) {
			return;
		}

		const isFileDrag = (event: DragEvent) =>
			!!event.dataTransfer?.types.includes("Files");
		const handleDragOver = (event: DragEvent) => {
			if (!isFileDrag(event)) {
				return;
			}
			event.preventDefault();
			setIsDragOver(true);
		};
		const handleDragLeave = (event: DragEvent) => {
			// relatedTarget is only null once the drag leaves the window
			if (!event.relatedTarget) {
				setIsDragOver(false);
			}
		};
		const handleDrop = (event: DragEvent) => {
			if (!isFileDrag(event)) {
				return;
			}
			event.preventDefault();
			setIsDragOver(false);
			void upload.uploadFiles(imageFiles(event.dataTransfer?.files));
		};
		const handlePaste = (event: ClipboardEvent) => {
			const files = imageFiles(event.clipboardData?.files);
			if (files.length === 0) {
				return;
			}
			event.preventDefault();
			void upload.uploadFiles(files);
		};

		window.addEventListener("dragover", handleDragOver);
		window.addEventListener("dragleave", handleDragLeave);
		window.addEventListener("drop", handleDrop);
		window.addEventListener("paste", handlePaste);

		return () => {
			window.removeEventListener("dragover", handleDragOver);
			window.removeEventListener("dragleave", handleDragLeave);
			window.removeEventListener("drop", handleDrop);
			window.removeEventListener("paste", handlePaste);
		};
	}, [open, upload.uploadFiles]);

	useEffect(() => {
		const element = loadMoreRef.current;
		if (!element || !hasNextPage) {
			return;
		}

		const observer = new IntersectionObserver((entries) => {
			if (entries[0]?.isIntersecting && !isFetchingNextPage) {
				fetchNextPage();
			}
		});
		observer.observe(element);

		return () => observer.disconnect();
	}, [fetchNextPage, hasNextPage, isFetchingNextPage]);

	const groups = groupAssetsByDate(items);
	const isFull = newImageCount(selected) >= remainingSlots;

	const toggle = (url: string) => {
		setSelected((previous) => {
			if (previous.includes(url)) {
				return previous.filter((item) => item !== url);
			}
			if (
				!existingUrls.includes(url) &&
				newImageCount(previous) >= remainingSlots
			) {
				return previous;
			}
			return [...previous, url];
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex h-[85vh] max-w-5xl flex-col gap-0 p-0">
				<DialogHeader className="border-b border-border/60 px-6 py-4">
					<DialogTitle className="text-lg">{t("title")}</DialogTitle>
				</DialogHeader>

				<input
					ref={upload.inputRef}
					type="file"
					accept="image/*"
					multiple
					className="hidden"
					onChange={upload.handleChange}
				/>
				{/* Same look as the generator's dropzone. On phones nothing gets
				    dragged and few people paste, so it shrinks to a one-line button
				    instead of pushing the grid down — also on short screens like a
				    landscape phone, where 85vh can't fit the tall version (sm-tall) */}
				<button
					type="button"
					disabled={upload.isUploading}
					onClick={upload.openFilePicker}
					className={cn(
						"mx-6 mt-4 flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-dashed transition-colors sm-tall:h-auto sm-tall:flex-col sm-tall:rounded-2xl sm-tall:py-6",
						upload.isUploading
							? "cursor-not-allowed border-zinc-300 bg-zinc-100"
							: isDragOver
								? "border-blue-400 bg-blue-50"
								: "border-zinc-300 bg-zinc-100 hover:border-zinc-400 hover:bg-zinc-200/60",
					)}
				>
					{upload.isUploading ? (
						<Loader2Icon className="size-4 animate-spin text-zinc-600 sm-tall:size-6" />
					) : (
						<UploadIcon className="size-4 text-zinc-700 sm-tall:size-6" />
					)}
					<span className="text-sm font-medium text-zinc-700 sm-tall:rounded-full sm-tall:bg-zinc-900 sm-tall:px-5 sm-tall:py-2 sm-tall:text-xs sm-tall:text-white">
						{t("upload")}
					</span>
					<span className="hidden text-xs text-zinc-500 sm-tall:block">
						{t("dropOrPaste")}
					</span>
				</button>

				<AssetToolbar
					className="px-6 py-3"
					tabs={PICKER_ASSET_FILTERS.map((assetFilter) => ({
						id: assetFilter,
						label: tAssets(`tabs.${assetFilter}`),
					}))}
					activeTab={tab}
					onTabChange={setTab}
				/>

				<div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
					{isLoading ? (
						<div className="flex h-full items-center justify-center">
							<Spinner className="h-8 w-8" />
						</div>
					) : error ? (
						<p className="flex h-full items-center justify-center text-sm text-destructive">
							{t("loadFailed")}
						</p>
					) : groups.length === 0 ? (
						<p className="flex h-full items-center justify-center text-sm text-muted-foreground">
							{t("empty")}
						</p>
					) : (
						<>
							{groups.map((group) => (
								<div key={group.date} className="mb-6">
									<h3 className="mb-3 text-sm font-light tracking-wide text-muted-foreground">
										{group.date}
									</h3>
									<div className="grid grid-cols-3 gap-3 sm:grid-cols-5 md:grid-cols-6">
										{group.items.map((item) => {
											const isSelected =
												selected.includes(item.url);
											const isBroken =
												brokenUrls.includes(item.url);
											const disabled =
												isBroken ||
												(isFull &&
													!isSelected &&
													!existingUrls.includes(
														item.url,
													));
											return (
												<button
													key={item.key}
													type="button"
													disabled={disabled}
													onClick={() =>
														toggle(item.url)
													}
													title={item.prompt}
													className={cn(
														"relative aspect-square overflow-hidden rounded-lg bg-muted transition-all",
														isSelected
															? "ring-2 ring-primary ring-offset-2"
															: "hover:opacity-85",
														disabled
															? "cursor-not-allowed opacity-40"
															: "cursor-pointer",
													)}
												>
													{isBroken ? (
														<span className="flex h-full w-full items-center justify-center text-muted-foreground">
															<ImageOffIcon className="h-5 w-5" />
														</span>
													) : (
														<img
															src={item.url}
															alt={
																item.prompt ??
																""
															}
															loading="lazy"
															className="h-full w-full object-cover"
															onError={() =>
																setBrokenUrls(
																	(
																		previous,
																	) =>
																		previous.includes(
																			item.url,
																		)
																			? previous
																			: [
																					...previous,
																					item.url,
																				],
																)
															}
														/>
													)}
													{isSelected && (
														<span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
															<CheckIcon className="h-3 w-3" />
														</span>
													)}
												</button>
											);
										})}
									</div>
								</div>
							))}
							<div
								ref={loadMoreRef}
								className="flex justify-center py-2"
							>
								{isFetchingNextPage && (
									<Spinner className="h-6 w-6" />
								)}
							</div>
						</>
					)}
				</div>

				<div className="flex items-center justify-between gap-3 border-t border-border/60 px-6 py-4">
					<p className="text-xs text-muted-foreground">
						{t("selected", { count: selected.length })} ·{" "}
						{t("slotsLeft", {
							remaining: remainingSlots - newImageCount(selected),
						})}
					</p>
					<div className="flex gap-2">
						<Button
							type="button"
							variant="outline"
							className="border-base-300 text-base-content/70 hover:bg-base-200"
							onClick={() => onOpenChange(false)}
						>
							{t("cancel")}
						</Button>
						<Button
							type="button"
							variant="primary"
							// A batch still uploading would land in the selection
							// after the dialog closes and show up pre-ticked next time
							disabled={
								selected.length === 0 || upload.isUploading
							}
							onClick={() => {
								onConfirm(selected);
								onOpenChange(false);
							}}
						>
							{t("confirm")}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
